<template>
  <div class="terrain-responses-table">
    <p v-if="loading" class="terrain-responses-table__status">Cargando respuestas…</p>
    <p v-else-if="errorMessage" class="terrain-responses-table__error" role="alert">{{ errorMessage }}</p>
    <p v-else-if="!items.length" class="terrain-responses-table__empty">No hay respuestas para esta campaña.</p>

    <div v-if="editMode && dirtyCount > 0" class="terrain-responses-table__save-bar">
      <span>{{ dirtyCount }} respuesta{{ dirtyCount === 1 ? "" : "s" }} con cambios sin guardar</span>
      <button type="button" class="terrain-responses-table__save-btn" :disabled="saving" @click="saveAllChanges">
        {{ saving ? "Guardando…" : "Guardar cambios" }}
      </button>
    </div>

    <div v-if="items.length" class="terrain-responses-table__scroll">
      <table class="terrain-responses-table__table">
        <thead>
          <tr>
            <th>ID</th>
            <th class="sortable" @click="onSort('submittedAt')">Fecha envío <font-awesome-icon :icon="getSortIcon('submittedAt')" /></th>
            <th class="sortable" @click="onSort('respondent')">Respondente <font-awesome-icon :icon="getSortIcon('respondent')" /></th>
            <th class="sortable" @click="onSort('status')">Estado <font-awesome-icon :icon="getSortIcon('status')" /></th>
            <th>Ubicación</th>
            <th v-for="question in questionColumns" :key="question.questionId" :title="question.label">{{ question.label }}</th>
            <th class="terrain-responses-table__actions-col">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in sortedItems"
            :key="item.id"
            :class="{ 'terrain-responses-table__row--error': rowErrors[item.id] }"
            :title="rowErrors[item.id] || null"
          >
            <td :title="item.id" class="terrain-responses-table__id">#{{ shortId(item.id) }}</td>
            <td>{{ dateLabel(item.submittedAt) }}</td>
            <td>{{ respondentLabel(item) }}</td>
            <td>
              <span class="terrain-responses-table__tag" :class="`terrain-responses-table__tag--${item.status}`">{{ statusLabel(item.status) }}</span>
            </td>
            <td>
              <button
                v-if="hasLocation(item)"
                type="button"
                class="terrain-responses-table__location-btn"
                :class="{ 'terrain-responses-table__location-btn--dirty': isLocationDirty(item) }"
                @click="openLocationModal(item)"
              >
                <font-awesome-icon icon="map-marker-alt" /> {{ formatCoords(locationCoordinates(item)) }}
              </button>
              <span v-else class="terrain-responses-table__muted">Sin ubicación</span>
            </td>
            <td
              v-for="question in questionColumns"
              :key="question.questionId"
              :class="{ 'terrain-responses-table__cell--editing': editMode && isRowEditable(item) && isInlineEditable(question) }"
              :title="!isInlineEditable(question) || !(editMode && isRowEditable(item)) ? answerValue(item, question) : null"
            >
              <template v-if="editMode && isRowEditable(item) && isInlineEditable(question)">
                <select
                  v-if="question.type === 'singleSelect'"
                  :class="{ 'terrain-responses-table__input--dirty': isCellDirty(item, question) }"
                  :value="cellValue(item, question)"
                  @change="updateCell(item, question, $event.target.value)"
                >
                  <option value="">—</option>
                  <option v-for="option in question.options || []" :key="option.optionId" :value="option.optionId">{{ option.label }}</option>
                </select>
                <input
                  v-else-if="question.type === 'check'"
                  type="checkbox"
                  :class="{ 'terrain-responses-table__input--dirty': isCellDirty(item, question) }"
                  :checked="Boolean(cellValue(item, question))"
                  @change="updateCell(item, question, $event.target.checked)"
                />
                <input
                  v-else
                  :type="question.type === 'number' ? 'number' : question.type === 'date' ? 'date' : 'text'"
                  :class="{ 'terrain-responses-table__input--dirty': isCellDirty(item, question) }"
                  :value="cellValue(item, question)"
                  @input="updateCell(item, question, question.type === 'number' ? ($event.target.value === '' ? null : Number($event.target.value)) : $event.target.value)"
                />
              </template>
              <AttachmentGallery
                v-else-if="isAttachmentQuestion(question) && attachmentAnswer(item, question)"
                compact
                :attachment-ids="attachmentAnswer(item, question).attachmentIds"
                :attachment-names="attachmentAnswer(item, question).attachmentNames || {}"
                :kind="question.type"
                :label="question.label"
              />
              <template v-else>{{ answerValue(item, question) }}</template>
            </td>
            <td class="terrain-responses-table__actions-cell">
              <div class="terrain-responses-table__actions">
                <button
                  type="button"
                  class="terrain-responses-table__menu-btn"
                  :disabled="!availableTargetsFor(item).length || rowTransitioning[item.id]"
                  :title="availableTargetsFor(item).length ? 'Cambiar estado' : 'Sin transiciones disponibles'"
                  :aria-label="`Cambiar estado de la respuesta de ${respondentLabel(item)}`"
                  @click.stop="toggleRowMenu(item.id, $event)"
                >
                  <font-awesome-icon icon="ellipsis-h" />
                </button>
                <ul v-if="openMenuRowId === item.id" class="terrain-responses-table__menu" :style="menuStyle" v-click-outside="closeRowMenu">
                  <li v-for="target in availableTargetsFor(item)" :key="target" @click="chooseTarget(item, target)">
                    {{ targetLabel(target) }}
                  </li>
                </ul>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <modal name="terrain-responses-table-location" :width="480" height="auto" :adaptive="true" :scrollable="true">
      <div class="terrain-responses-table__location-modal">
        <div class="terrain-responses-table__modal-header">
          <strong>Ubicación</strong>
          <button type="button" title="Cerrar" @click="$modal.hide('terrain-responses-table-location')"><font-awesome-icon icon="times" /></button>
        </div>
        <p v-if="locationModalEditable" class="terrain-responses-table__location-hint">Toque el mapa para mover el marcador.</p>
        <LocationPickerMap
          v-if="locationModalCoordinates"
          :coordinates="locationModalCoordinates"
          :readonly="!locationModalEditable"
          @update:coordinates="onLocationPicked"
        />
        <div v-if="locationModalEditable" class="terrain-responses-table__comment-modal-actions">
          <button type="button" @click="$modal.hide('terrain-responses-table-location')">Cancelar</button>
          <button type="button" @click="confirmLocationEdit">Confirmar</button>
        </div>
      </div>
    </modal>

    <modal name="terrain-responses-table-comment" :width="420" height="auto" :adaptive="true">
      <div class="terrain-responses-table__comment-modal">
        <h3>{{ commentModalTarget === 'rejected' ? 'Rechazar' : 'Retirar' }} respuesta</h3>
        <p>Este comentario queda registrado junto con la transición.</p>
        <label class="terrain-responses-table__comment-label">
          Comentario
          <textarea v-model="commentModalText" rows="4" />
        </label>
        <p v-if="commentModalError" class="terrain-responses-table__error" role="alert">{{ commentModalError }}</p>
        <div class="terrain-responses-table__comment-modal-actions">
          <button type="button" @click="$modal.hide('terrain-responses-table-comment')">Cancelar</button>
          <button type="button" @click="confirmCommentModal">Confirmar</button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import LocationPickerMap from "../terrain/form/LocationPickerMap.vue";
import AttachmentGallery from "./AttachmentGallery.vue";
import { createTerrainApi } from "../../services/terrain/api.js";
import {
  AVAILABLE_TARGETS_BY_STATUS,
  COMMENT_REQUIRED_TARGETS,
  PUBLISH_REQUIRED_TARGETS,
  TARGET_LABELS,
  EDITABLE_STATUSES,
  statusLabel as sharedStatusLabel,
  runTransition,
  transitionErrorMessage,
  createIdempotencyKey,
} from "../../services/terrain/reviewWorkflow.js";

// Tipos de pregunta editables inline en la tabla (modo edición). El resto
// (incluidos los de COMPLEX_QUESTION_TYPES y multiSelect) se sigue viendo
// como texto — se editan desde el detalle en la vista de tarjetas.
const INLINE_EDITABLE_TYPES = ["shortText", "longText", "number", "date", "singleSelect", "check"];

// Tipos de pregunta cuyo valor no cabe legible en una celda de tabla
// (tablas/repetidores anidados, adjuntos, un GPS propio de la pregunta) —
// se deja un placeholder en vez de intentar formatearlos; ver el detalle
// completo (TerrainFormRenderer, modo tarjetas) para esos casos.
const COMPLEX_QUESTION_TYPES = ["table", "repeater", "photo", "file", "location"];

function optionLabel(question, rawValue) {
  const option = (question.options || []).find((candidate) => candidate.optionId === rawValue);
  return option ? option.label : String(rawValue);
}

function formatAnswerValue(question, value) {
  if (value === undefined || value === null || value === "") return "—";
  if (COMPLEX_QUESTION_TYPES.includes(question.type)) return "(ver detalle)";
  if (question.type === "check") return value ? "Sí" : "No";
  if (Array.isArray(value)) {
    return value.map((entry) => (question.options ? optionLabel(question, entry) : String(entry))).join(", ");
  }
  if (question.options && question.options.length) return optionLabel(question, value);
  return String(value);
}

export default {
  name: "TerrainResponsesTable",
  components: { LocationPickerMap, AttachmentGallery },
  props: {
    campaignId: { type: String, required: true },
    editMode: { type: Boolean, default: false },
  },
  data() {
    return {
      items: [],
      definition: null,
      loading: true,
      errorMessage: "",
      sortKey: "submittedAt",
      sortDir: "desc",
      openMenuRowId: null,
      // menuStyle: el menú de acciones vive dentro de .terrain-responses-table__scroll
      // (overflow-x:auto — por la regla de CSS que fuerza overflow-y a auto
      // apenas overflow-x no es visible, esa misma celda recorta cualquier
      // hijo position:absolute que se salga verticalmente). position:fixed +
      // coordenadas calculadas a mano desde el botón (mismo patrón ya usado
      // en TagMultiSelect.vue para el mismo problema) lo saca de ese recorte.
      menuStyle: {},
      locationModalCoordinates: null,
      locationModalEditable: false,
      locationModalItem: null,
      pendingLocationCoordinates: null,
      commentModalRow: null,
      commentModalTarget: null,
      commentModalText: "",
      commentModalError: "",
      rowTransitioning: {},
      // edits: responseId -> { answers?: {questionId: value}, location?: [lon,lat] }.
      // Solo contiene lo que el usuario efectivamente tocó en modo edición;
      // se vacía por completo al guardar (por fila, según éxito) o al salir
      // de modo edición (ver watch.editMode).
      edits: {},
      rowErrors: {},
      saving: false,
    };
  },
  computed: {
    terrainApi() { return createTerrainApi(this.$http, this.$store.getters.backendurl); },
    permissions() { return (this.$store.getters.getPermissions || {}).terrain || {}; },
    questionColumns() {
      if (!this.definition) return [];
      return (this.definition.sections || []).flatMap((section) => section.questions || []);
    },
    dirtyCount() { return Object.keys(this.edits).length; },
    sortedItems() {
      const items = [...this.items];
      const dir = this.sortDir === "asc" ? 1 : -1;
      items.sort((a, b) => {
        const va = this.sortValue(a);
        const vb = this.sortValue(b);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
      return items;
    },
  },
  created() {
    this.load();
  },
  watch: {
    edits: {
      deep: true,
      handler() { this.$emit("dirty-change", this.dirtyCount > 0); },
    },
    editMode(next) {
      // El padre (TerrainCampaignResponses.vue) ya confirmó el descarte
      // antes de bajar la prop a false (ver dirty-change) — acá solo se
      // limpia el estado local, nunca se vuelve a preguntar.
      if (!next) {
        this.edits = {};
        this.rowErrors = {};
      }
    },
  },
  methods: {
    sortValue(item) {
      if (this.sortKey === "respondent") return this.respondentLabel(item).toLowerCase();
      if (this.sortKey === "status") return item.status || "";
      return item.submittedAt || "";
    },
    onSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDir = key === "submittedAt" ? "desc" : "asc";
      }
    },
    getSortIcon(key) {
      if (this.sortKey !== key) return "sort";
      return this.sortDir === "asc" ? "sort-up" : "sort-down";
    },
    async load() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const response = await this.terrainApi.listResponses({ page: 1, size: 200, campaignId: this.campaignId });
        this.items = (response.data && response.data.items) || [];
        // La definición de plantilla es la misma para toda la campaña — se
        // pide una sola vez, vía el detalle de la primera respuesta (mismo
        // endpoint que ya usa TerrainReviewQueue.vue para su panel), en vez
        // de agregar un camino nuevo al backend solo para esto.
        if (this.items.length) {
          const detail = await this.terrainApi.getResponse(this.items[0].id);
          this.definition = (detail.data && detail.data.definition) || null;
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.replace({ name: "inicio", query: { returnTo: this.$route.fullPath } });
          return;
        }
        this.errorMessage = "No fue posible cargar las respuestas. Intente nuevamente.";
      } finally {
        this.loading = false;
      }
    },
    shortId(id) { return String(id).slice(-6).toUpperCase(); },
	respondentLabel(item) {
	  if (!item) return "Respondente desconocido";
	  if (item.recipientEmail && item.respondentUsername) return `${item.recipientEmail} (${item.respondentUsername})`;
	  return item.recipientEmail || item.respondentUsername || item.recipientId || item.respondentId || "Respondente desconocido";
	},
    dateLabel(value) {
      if (!value) return "Sin fecha";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "Sin fecha" : date.toLocaleString();
    },
    statusLabel(status) { return sharedStatusLabel(status); },
    targetLabel(target) { return TARGET_LABELS[target] || target; },
    hasLocation(item) {
      const location = item && item.location;
      return !!location && location.type === "Point" && Array.isArray(location.coordinates);
    },
    formatCoords(coordinates) {
      const [lon, lat] = coordinates;
      return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
    },
    // isRowEditable: la fila admite corrección — mismo criterio que
    // correctionEditableStatuses en services/terrain/correction_service.go
	// (submitted/en_revision/rechazada/aprobada; retirada queda de solo
	// lectura aunque el modo edición esté activo).
    isRowEditable(item) { return EDITABLE_STATUSES.includes(item.status); },
    isInlineEditable(question) { return INLINE_EDITABLE_TYPES.includes(question.type); },
    rawAnswerValue(item, question) {
      const answer = (item.answers || []).find((candidate) => candidate.questionId === question.questionId);
      return answer ? answer.value : null;
    },
    cellValue(item, question) {
      const rowEdits = this.edits[item.id];
      if (rowEdits && rowEdits.answers && Object.prototype.hasOwnProperty.call(rowEdits.answers, question.questionId)) {
        return rowEdits.answers[question.questionId];
      }
      return this.rawAnswerValue(item, question);
    },
    updateCell(item, question, value) {
      if (!this.edits[item.id]) this.$set(this.edits, item.id, {});
      if (!this.edits[item.id].answers) this.$set(this.edits[item.id], "answers", {});
      this.$set(this.edits[item.id].answers, question.questionId, value);
    },
    isCellDirty(item, question) {
      const rowEdits = this.edits[item.id];
      return Boolean(rowEdits && rowEdits.answers && Object.prototype.hasOwnProperty.call(rowEdits.answers, question.questionId));
    },
    isLocationDirty(item) {
      const rowEdits = this.edits[item.id];
      return Boolean(rowEdits && rowEdits.location);
    },
    locationCoordinates(item) {
      const rowEdits = this.edits[item.id];
      return (rowEdits && rowEdits.location) || item.location.coordinates;
    },
    openLocationModal(item) {
      this.locationModalEditable = this.editMode && this.isRowEditable(item);
      this.locationModalItem = item;
      this.locationModalCoordinates = this.locationCoordinates(item);
      this.pendingLocationCoordinates = this.locationModalCoordinates;
      this.$modal.show("terrain-responses-table-location");
    },
    onLocationPicked(coordinates) {
      this.pendingLocationCoordinates = coordinates;
    },
    confirmLocationEdit() {
      if (!this.locationModalItem) return;
      if (!this.edits[this.locationModalItem.id]) this.$set(this.edits, this.locationModalItem.id, {});
      this.$set(this.edits[this.locationModalItem.id], "location", this.pendingLocationCoordinates);
      this.$modal.hide("terrain-responses-table-location");
    },
    answerValue(item, question) {
      const answer = (item.answers || []).find((candidate) => candidate.questionId === question.questionId);
      return formatAnswerValue(question, answer ? answer.value : undefined);
    },
    isAttachmentQuestion(question) { return question.type === "photo" || question.type === "file"; },
    // attachmentAnswer devuelve el value crudo ({attachmentIds, attachmentNames})
    // de una pregunta photo/file, o null si no hay adjuntos — permite que la
    // celda muestre un badge clickeable (AttachmentGallery) en vez del
    // placeholder de texto "(ver detalle)" que usa formatAnswerValue para el
    // resto de los COMPLEX_QUESTION_TYPES.
    attachmentAnswer(item, question) {
      const answer = (item.answers || []).find((candidate) => candidate.questionId === question.questionId);
      const value = answer && answer.value;
      if (!value || !Array.isArray(value.attachmentIds) || !value.attachmentIds.length) return null;
      return value;
    },
    toggleRowMenu(id, event) {
      if (this.openMenuRowId === id) { this.openMenuRowId = null; return; }
      const rect = event.currentTarget.getBoundingClientRect();
      this.menuStyle = { position: "fixed", top: `${rect.bottom + 4}px`, left: "auto", right: `${window.innerWidth - rect.right}px` };
      this.openMenuRowId = id;
    },
    closeRowMenu() { this.openMenuRowId = null; },
    // availableTargetsFor: mismas transiciones válidas que TerrainReviewQueue.vue
    // (AVAILABLE_TARGETS_BY_STATUS), pero acá además se ocultan las que
    // exigen "publish" si el usuario logueado no tiene esa capacidad —
    // TerrainReviewQueue.vue no lo hacía (las mostraba igual y el backend
    // recién rechazaba con 403 al hacer clic); no se corrige ese gap ahí
    // para no tocar comportamiento existente sin que lo hayan pedido, pero
    // en código nuevo no tiene sentido repetir el mismo hueco.
    availableTargetsFor(item) {
      const targets = AVAILABLE_TARGETS_BY_STATUS[item.status] || [];
      if (this.permissions.publish) return targets;
      return targets.filter((target) => !PUBLISH_REQUIRED_TARGETS.includes(target));
    },
    chooseTarget(item, target) {
      this.closeRowMenu();
      if (COMMENT_REQUIRED_TARGETS.includes(target)) {
        this.commentModalRow = item;
        this.commentModalTarget = target;
        this.commentModalText = "";
        this.commentModalError = "";
        this.$modal.show("terrain-responses-table-comment");
        return;
      }
      this.$modal.show("dialog", {
        text: `¿${this.targetLabel(target)} la respuesta de ${this.respondentLabel(item)}?`,
        buttons: [
          { title: "Cancelar" },
          { title: this.targetLabel(target), handler: () => { this.$modal.hide("dialog"); return this.applyRowTransition(item, target); } },
        ],
      });
    },
    confirmCommentModal() {
      if (!this.commentModalText.trim()) {
        this.commentModalError = "Ingrese un comentario antes de continuar.";
        return;
      }
      const item = this.commentModalRow;
      const target = this.commentModalTarget;
      const comment = this.commentModalText.trim();
      this.$modal.hide("terrain-responses-table-comment");
      this.applyRowTransition(item, target, comment);
    },
    async applyRowTransition(item, target, comment) {
      this.$set(this.rowTransitioning, item.id, true);
      try {
        const data = await runTransition(this.terrainApi, item, target, comment);
        this.items = this.items.map((candidate) => (
          candidate.id === item.id ? { ...candidate, status: data.status, resourceVersion: data.resourceVersion } : candidate
        ));
        this.$store.dispatch("toast/show", { message: `Respuesta ${this.statusLabel(data.status).toLowerCase()} correctamente.`, type: "success" });
      } catch (error) {
        this.$store.dispatch("toast/show", { message: transitionErrorMessage(error), type: "error" });
      } finally {
        this.$set(this.rowTransitioning, item.id, false);
      }
    },
    // saveAllChanges: guarda todas las filas con cambios pendientes, una
    // corrección (POST /responses/:id/corrections) por respuesta afectada.
    // answers se manda completo (originales + editados encima) porque el
    // endpoint reemplaza el set entero. Reporta éxitos/fallos por fila
    // (mismo criterio que runBulkTransition en TerrainReviewQueue.vue): las
    // filas que fallan conservan su edición y quedan marcadas en rojo para
    // reintentar; las exitosas se limpian de `edits`.
    async saveAllChanges() {
      this.saving = true;
      const ids = Object.keys(this.edits);
      let okCount = 0;
      let failCount = 0;
      for (const id of ids) {
        const item = this.items.find((candidate) => candidate.id === id);
        const rowEdits = this.edits[id];
        if (!item || !rowEdits) { this.$delete(this.edits, id); continue; }

        const payload = { expectedVersion: item.resourceVersion };
        let newAnswers = item.answers;
        if (rowEdits.answers && Object.keys(rowEdits.answers).length) {
          const byQuestionId = new Map((item.answers || []).map((answer) => [answer.questionId, answer.value]));
          Object.keys(rowEdits.answers).forEach((questionId) => byQuestionId.set(questionId, rowEdits.answers[questionId]));
          newAnswers = Array.from(byQuestionId.entries()).map(([questionId, value]) => ({ questionId, value }));
          payload.answers = newAnswers;
        }
        let newLocation = item.location;
        if (rowEdits.location) {
          payload.location = { type: "Point", coordinates: rowEdits.location, correctedAt: new Date().toISOString() };
          newLocation = { ...item.location, type: "Point", coordinates: rowEdits.location };
        }

        this.$set(this.rowTransitioning, id, true);
        try {
          const result = await this.terrainApi.correctResponse(id, payload, item.resourceVersion, createIdempotencyKey());
          const data = result.data;
          this.items = this.items.map((candidate) => (
            candidate.id === id ? { ...candidate, resourceVersion: data.resourceVersion, answers: newAnswers, location: newLocation } : candidate
          ));
          this.$delete(this.edits, id);
          this.$delete(this.rowErrors, id);
          okCount += 1;
        } catch (error) {
          this.$set(this.rowErrors, id, transitionErrorMessage(error));
          failCount += 1;
        } finally {
          this.$set(this.rowTransitioning, id, false);
        }
      }
      this.saving = false;
      const message = failCount === 0
        ? `Se guardaron ${okCount} respuesta${okCount === 1 ? "" : "s"} correctamente.`
        : `${okCount} guardada${okCount === 1 ? "" : "s"}, ${failCount} con error. Revise las filas marcadas.`;
      this.$store.dispatch("toast/show", { message, type: failCount === 0 ? "success" : "error" });
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-responses-table__status,
.terrain-responses-table__empty {
  color: var(--color-ghost);
}
.terrain-responses-table__error { color: var(--color-danger); }

.terrain-responses-table__save-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--terrain-space-md);
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  background: var(--terrain-color-warning-light);
  border: 1px solid var(--color-warning);
  border-radius: var(--terrain-radius-md);
  color: var(--color-graphite);
  font-size: 0.875em;
}
.terrain-responses-table__save-btn {
  border: 0;
  border-radius: var(--terrain-radius-xl);
  background: var(--color-navy);
  color: var(--color-white);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);

  &:disabled { background: var(--color-ghost); cursor: not-allowed; }
  &:hover:not(:disabled) { background: var(--color-steel); }
}

// Especificidad: igual trampa que .terrain-responses-table__actions-cell
// más abajo — la fila cebra (tbody tr:nth-child(odd)) tiene más
// especificidad que una sola clase, así que hay que calificarla igual para
// que el rojo de error gane en filas impares.
.terrain-responses-table__table tbody tr.terrain-responses-table__row--error { background: var(--terrain-color-danger-light); }

.terrain-responses-table__scroll { overflow-x: auto; }

.terrain-responses-table__table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  font-size: 0.875em;
}

.terrain-responses-table__table th,
.terrain-responses-table__table td {
  padding: 0.6em 0.8em;
  border-bottom: 1px solid var(--color-line);
  text-align: left;
  vertical-align: middle;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.terrain-responses-table__table thead th {
  position: sticky;
  top: 0;
  background: var(--color-navy);
  color: var(--color-white);
  font-weight: 600;
  white-space: nowrap;
  z-index: 1;
}

.terrain-responses-table__table th.sortable { cursor: pointer; user-select: none; }
.terrain-responses-table__table th.sortable:hover { background: var(--color-steel); }

.terrain-responses-table__table tbody tr:nth-child(odd) { background: var(--color-mist); }
.terrain-responses-table__table tbody tr:hover { background: var(--terrain-color-warning-light); }

.terrain-responses-table__id { font-family: monospace; }

.terrain-responses-table__tag {
  display: inline-block;
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-xl);
  background: var(--color-mist);
  color: var(--color-graphite);
  font-size: var(--terrain-font-caption);
  font-weight: 700;
  padding: 2px var(--terrain-space-sm);
  white-space: nowrap;

  &--submitted { background: var(--terrain-color-warning-light); border-color: var(--color-orange); color: var(--color-orange); }
  &--en_revision, &--corregida { background: var(--color-mist); border-color: var(--color-steel); color: var(--color-steel); }
  &--observada { background: var(--terrain-color-warning-light); border-color: var(--color-warning); color: var(--color-warning); }
  &--aprobada { background: var(--terrain-color-success-light); border-color: var(--color-success); color: var(--color-success); }
  &--rechazada { background: var(--terrain-color-danger-light); border-color: var(--color-danger); color: var(--color-danger); }
  &--retirada { background: var(--color-mist); border-color: var(--color-line); color: var(--color-ghost); }
}

.terrain-responses-table__location-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  border: 0;
  background: transparent;
  color: var(--color-steel);
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  text-decoration: underline;
}
.terrain-responses-table__muted { color: var(--color-ghost); }
.terrain-responses-table__location-btn--dirty { color: var(--color-orange); font-weight: 600; }

// Mismo problema de especificidad que .terrain-responses-table__actions-cell
// más abajo: la regla genérica de celda (overflow:hidden + nowrap) recorta
// cualquier <select>/<input> que no quepa; se califica igual para que las
// celdas en edición puedan mostrar su control completo.
.terrain-responses-table__table td.terrain-responses-table__cell--editing {
  overflow: visible;
  white-space: normal;

  input, select {
    width: 100%;
    min-width: 90px;
    font: inherit;
    padding: 0.3em 0.4em;
    border: 1px solid var(--color-line);
    border-radius: 4px;
  }
  input[type="checkbox"] { width: auto; min-width: 0; }
}
.terrain-responses-table__input--dirty { border-color: var(--color-orange) !important; background: var(--terrain-color-warning-light); }

.terrain-responses-table__actions-col { width: 60px; }
// Especificidad: ".terrain-responses-table__table td" (clase + elemento) le
// gana a un solo selector de clase — sin calificar acá igual, este override
// de overflow nunca aplicaba y el menú desplegable quedaba recortado
// invisible dentro de la celda pese a tener display/visibility/z-index
// correctos (confirmado con document.elementFromPoint en vivo).
.terrain-responses-table__table td.terrain-responses-table__actions-cell { overflow: visible; white-space: nowrap; }
.terrain-responses-table__actions { position: relative; display: inline-block; }

.terrain-responses-table__menu-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--color-line);
  border-radius: 4px;
  background: var(--color-white);
  color: var(--color-graphite);
  cursor: pointer;

  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:hover:not(:disabled) { background: var(--color-mist); }
}

.terrain-responses-table__menu {
  // position/top/left/right vienen del :style inline (menuStyle, ver
  // toggleRowMenu) — position:fixed calculado desde el botón, no
  // position:absolute, porque .terrain-responses-table__scroll
  // (overflow-x:auto) recorta cualquier hijo absolute que se salga
  // verticalmente (overflow-x no-visible fuerza overflow-y a auto).
  z-index: 20;
  min-width: 160px;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: 4px;
  box-shadow: 0 6px 16px rgba(14, 42, 64, 0.16);

  li {
    padding: 8px 10px;
    border-radius: 3px;
    cursor: pointer;
    color: var(--color-graphite);
    font-size: 0.85em;
    white-space: nowrap;

    &:hover { background: var(--color-mist); }
  }
}

.terrain-responses-table__location-modal,
.terrain-responses-table__comment-modal {
  padding: 16px;
  background: var(--color-white);
}
.terrain-responses-table__location-hint { color: var(--color-ghost); font-size: 0.85em; margin: 0 0 8px; }
.terrain-responses-table__modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;

  button { border: 0; background: transparent; cursor: pointer; color: var(--color-graphite); }
}
.terrain-responses-table__comment-modal h3 { margin: 0 0 6px; }
.terrain-responses-table__comment-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-weight: 600;
  margin-top: 10px;

  textarea { font: inherit; padding: 8px; border: 1px solid var(--color-line); border-radius: 4px; }
}
.terrain-responses-table__comment-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}
</style>
